import { RouterOutput } from '@gongsegae/api';
import { useQuery } from '@tanstack/react-query';
import { format, startOfMonth, endOfMonth, addDays, addMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { usePickupDateTimeStore } from '@/stores/pickup-datetime-store';

type DailyOperation = RouterOutput['schedule']['dailyOperationsByDateRange'][number];
type DailyTimeSlot = RouterOutput['schedule']['dailyTimeSlotsByDate'][number];

const getDayName = (date: Date): string => {
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return days[date.getDay()];
};

export default function CalendarPage() {
  const navigate = useNavigate();
  const { pickupDate, pickupTime, setPickupDateTime, setPickupDate, setPickupTime, clearPickupDateTime } =
    usePickupDateTimeStore();

  const [currentMonth, setCurrentMonth] = useState(new Date());

  // 현재 월의 운영 정보 조회 (tRPC)
  const start = startOfMonth(addMonths(currentMonth, -1));
  const end = endOfMonth(addMonths(currentMonth, 1));
  const startStr = format(start, 'yyyy-MM-dd');
  const endStr = format(end, 'yyyy-MM-dd');
  const tomorrowStr = format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const effectiveStart = startStr > tomorrowStr ? startStr : tomorrowStr;

  const {
    data: operationData,
    error: operationDataError,
    isLoading: isLoadingOperations,
  } = useQuery(
    trpc.schedule.dailyOperationsByDateRange.queryOptions({
      start: effectiveStart,
      end: endStr,
    })
  );

  const monthOperations: DailyOperation[] = useMemo(() => operationData || [], [operationData]);

  useEffect(() => {
    if (operationDataError) {
      toast.error('운영 정보를 불러오는데 실패했습니다.');
    }
  }, [operationDataError]);

  // 선택된 날짜의 타임슬롯 조회 (tRPC)
  const dateStr = pickupDate ? format(pickupDate, 'yyyy-MM-dd') : null;
  const {
    data: timeSlotsData,
    error: timeSlotsError,
    isLoading: isLoadingTimeSlots,
  } = useQuery(
    trpc.schedule.dailyTimeSlotsByDate.queryOptions(
      {
        date: dateStr ?? '',
      },
      {
        enabled: !!dateStr,
      }
    )
  );
  const dailyTimeSlots: DailyTimeSlot[] = useMemo(() => timeSlotsData || [], [timeSlotsData]);

  useEffect(() => {
    if (timeSlotsError) {
      toast.error('시간대 정보를 불러오는데 실패했습니다.');
    }
  }, [timeSlotsError]);

  // 날짜별 운영 정보 맵
  const operationMap = useMemo(() => {
    const map = new Map<string, DailyOperation>();
    monthOperations.forEach(op => {
      map.set(op.date, op);
    });
    return map;
  }, [monthOperations]);

  // 날짜가 선택 가능한지 확인
  const isDateDisabled = (date: Date): boolean => {
    // Pickup is allowed starting from the next day.
    const minimumPickupDate = new Date();
    minimumPickupDate.setHours(0, 0, 0, 0);
    minimumPickupDate.setDate(minimumPickupDate.getDate() + 1);
    if (date < minimumPickupDate) return true;

    // 운영 정보가 없거나 휴무일인 경우 선택 불가
    const dateStr = format(date, 'yyyy-MM-dd');
    const operation = operationMap.get(dateStr);

    return !operation || !operation.isOpen;
  };

  // 타임슬롯이 선택 가능한지 확인
  const isTimeSlotAvailable = (timeStr: string): boolean => {
    const slot = dailyTimeSlots.find(s => s.startTime === timeStr);
    if (!slot) return false;

    // 마감되었거나 최대 주문수에 도달한 경우 선택 불가
    return !slot.isClosed && slot.currentOrders < slot.maxOrders;
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date && !isDateDisabled(date)) {
      setPickupDate(date);
      setPickupTime(null); // 날짜 변경 시 시간 초기화
    }
  };

  const handleTimeSelect = (time: string) => {
    if (isTimeSlotAvailable(time)) {
      setPickupTime(time);
    }
  };

  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month);
  };

  const handleContinue = () => {
    if (!pickupDate || !pickupTime) {
      toast.error('날짜와 시간을 모두 선택해주세요.');
      return;
    }

    // Store에 선택된 날짜와 시간 저장
    setPickupDateTime(pickupDate, pickupTime);

    // 메뉴 페이지로 이동
    navigate('/menu');
  };

  // 선택된 날짜의 운영 정보
  const selectedDateOperation = pickupDate ? operationMap.get(format(pickupDate, 'yyyy-MM-dd')) : null;

  useEffect(() => {
    clearPickupDateTime();
  }, [clearPickupDateTime]);

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="px-5 py-4">
          <h1 className="text-xl font-bold text-center">픽업 시간 선택</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 px-5 py-6">
        {/* 안내 메시지 */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-900 font-medium">원하시는 픽업 날짜와 시간을 선택해주세요.</p>
          <p className="text-xs text-blue-700 mt-1">선택하신 시간에 따라 이용 가능한 메뉴가 달라질 수 있습니다.</p>
        </div>

        {/* 날짜 선택 섹션 */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">날짜 선택</h2>

          {pickupDate && (
            <div className="mb-4 p-3 bg-gray-50 rounded-md">
              <p className="text-base font-medium text-gray-700">
                선택된 날짜: {format(pickupDate, 'yyyy년 MM월 dd일', { locale: ko })} ({getDayName(pickupDate)})
              </p>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-4">
            {isLoadingOperations ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={pickupDate ?? undefined}
                    onSelect={handleDateSelect}
                    disabled={isDateDisabled}
                    month={currentMonth}
                    onMonthChange={handleMonthChange}
                    locale={ko}
                  />
                </div>
                <div className="text-xs text-gray-500 mt-4 space-y-1 text-center">
                  <div>• 운영일만 선택 가능합니다</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 시간 선택 섹션 */}
        {pickupDate && selectedDateOperation && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">시간 선택</h2>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                운영시간: {selectedDateOperation.startTime.slice(0, 5)} - {selectedDateOperation.endTime.slice(0, 5)}
              </p>
              {selectedDateOperation.notes && (
                <p className="text-xs text-gray-500 mt-1">{selectedDateOperation.notes}</p>
              )}
            </div>

            {isLoadingTimeSlots ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : dailyTimeSlots.length === 0 ? (
              <div className="text-center py-8 text-gray-500">예약 가능한 시간대가 없습니다.</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {dailyTimeSlots.map(slot => {
                  const disabled = !isTimeSlotAvailable(slot.startTime);

                  return (
                    <Button
                      key={slot.id}
                      variant={pickupTime === slot.startTime ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleTimeSelect(slot.startTime)}
                      disabled={disabled}
                      className={cn(
                        'h-auto py-2 px-3 flex flex-col items-center text-sm',
                        pickupTime === slot.startTime
                          ? 'bg-[#3D8AFF] text-white hover:bg-[#2C6FE6]'
                          : !disabled
                            ? 'border-gray-300 text-gray-700 hover:border-blue-500 hover:text-blue-600'
                            : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                      )}
                    >
                      <span className="font-medium">{slot.startTime.slice(0, 5)}</span>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* 선택 완료 버튼 */}
        <div className="sticky bottom-0 left-0 right-0 p-5 bg-white border-t border-gray-100 -mx-5">
          <Button
            onClick={handleContinue}
            disabled={!pickupDate || !pickupTime}
            className={cn(
              'w-full h-14 text-base font-semibold transition-colors',
              pickupDate && pickupTime
                ? 'bg-[#3D8AFF] text-white hover:bg-[#2C6FE6]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            )}
          >
            {pickupDate && pickupTime ? '메뉴 보러가기' : '날짜와 시간을 선택해주세요'}
          </Button>
        </div>
      </div>
    </div>
  );
}
